import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AttachmentPanel } from "../../components/equipment/attachment_panel";
import { EquipmentStatusBadge } from "../../components/equipment/equipment_status_badge";
import { EquipmentUsagePanel } from "../../components/equipment/equipment_usage_panel";
import { ServiceRecordPanel } from "../../components/equipment/service_record_panel";
import { useDrawer } from "../../components/use_drawer";
import { toDateTimeString } from "../../date_utils";
import {
    equipmentTypeLabel,
    formatDateOnly,
    getEquipment,
    listMaintenanceScheduleAdjustments,
    resolveEquipmentPermissions,
} from "../../services/equipment_service";
import { getCurrentUser } from "../../services/login";
import { EquipmentForm } from "./equipment_form";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("10px"),
        overflow: "auto",
    },
    header: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("12px"),
        ...FC.shorthands.border("1px", "solid", FC.tokens.colorNeutralStroke2),
        ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium),
        backgroundColor: FC.tokens.colorNeutralBackground1,
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        ...FC.shorthands.gap("10px"),
    },
    item: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("2px"),
    },
    section: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("12px"),
        ...FC.shorthands.border("1px", "solid", FC.tokens.colorNeutralStroke2),
        ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium),
        backgroundColor: FC.tokens.colorNeutralBackground1,
    },
    label: {
        color: FC.tokens.colorNeutralForeground3,
    },
    mono: {
        fontFamily: "Consolas, monospace",
        whiteSpace: "pre-wrap",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "13px",
    },
    cell: {
        textAlign: "left",
        ...FC.shorthands.padding("6px"),
        borderBottom: `1px solid ${FC.tokens.colorNeutralStroke2}`,
    },
});

const Field = ({ label, children }) => {
    const styles = useStyles();
    return (
        <div className={styles.item}>
            <FC.Caption1 className={styles.label}>{label}</FC.Caption1>
            <div>{children ?? "-"}</div>
        </div>
    );
};

const PhasePlaceholder = ({ phase, feature }) => (
    <FC.MessageBar intent="info">
        <FC.MessageBarBody>{`${feature}将在${phase}提供，本期未开放。`}</FC.MessageBarBody>
    </FC.MessageBar>
);

export const EquipmentDetail = () => {
    const styles = useStyles();
    const nav = useNavigate();
    const { equipmentId } = useParams();
    const permissions = useMemo(() => resolveEquipmentPermissions(getCurrentUser()), []);
    const { openDrawer, closeDrawer, drawer } = useDrawer({ style: { width: 720 } });

    const [detail, setDetail] = useState(null);
    const [adjustments, setAdjustments] = useState([]);
    const [selectedTab, setSelectedTab] = useState("basic");
    const [loadError, setLoadError] = useState("");
    const [adjustmentsError, setAdjustmentsError] = useState("");
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!equipmentId) {
            return;
        }
        setLoading(true);
        setLoadError("");
        try {
            const loaded = await getEquipment(equipmentId);
            setDetail(loaded);
        } catch (error) {
            console.error("Failed to load equipment detail:", error);
            setDetail(null);
            setLoadError(
                `${error?.message || "加载器材详情失败"}${error?.traceId ? `（追踪号 ${error.traceId}）` : ""}`
            );
        } finally {
            setLoading(false);
        }
        try {
            const items = await listMaintenanceScheduleAdjustments(equipmentId);
            setAdjustments(items);
            setAdjustmentsError("");
        } catch (error) {
            console.error("Failed to load maintenance schedule adjustments:", error);
            // 读取失败不能显示成"暂无记录"——那是把"读不到"报告成"业务上不存在"，
            // 用户会据此认为计划真的没被调整过。
            setAdjustments([]);
            setAdjustmentsError(
                `${error?.message || "加载保养计划调整记录失败"}${
                    error?.traceId ? `（追踪号 ${error.traceId}）` : ""
                }`
            );
        }
    }, [equipmentId]);

    useEffect(() => {
        load();
    }, [load]);

    const openEdit = () => {
        openDrawer({
            header: "编辑器材",
            body: (
                <EquipmentForm
                    item={detail}
                    isAdmin={permissions.isAdmin}
                    onSaved={({ close }) => {
                        load();
                        if (close) {
                            closeDrawer();
                        }
                    }}
                    onCancel={closeDrawer}
                />
            ),
        });
    };

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <FC.Button icon={<IC.ArrowLeftRegular />} onClick={() => nav("/equipment")}>
                    返回台账
                </FC.Button>
                <FC.Title3 className={styles.mono}>{detail?.assetCode || "-"}</FC.Title3>
                <FC.Body1>{detail?.name || ""}</FC.Body1>
                <FC.Badge appearance="tint" color="informative">
                    {equipmentTypeLabel(detail?.type)}
                </FC.Badge>
                <EquipmentStatusBadge kind="operationalStatus" value={detail?.operationalStatus} />
                {detail?.isArchived && (
                    <FC.Badge appearance="tint" color="subtle">
                        已归档
                    </FC.Badge>
                )}
                <div style={{ flex: 1 }} />
                {permissions.canWrite && detail && (
                    <FC.Button appearance="primary" icon={<IC.EditRegular />} onClick={openEdit}>
                        编辑
                    </FC.Button>
                )}
            </div>

            {loadError && (
                <FC.MessageBar intent="error">
                    <FC.MessageBarBody>{loadError}</FC.MessageBarBody>
                </FC.MessageBar>
            )}

            <FC.TabList selectedValue={selectedTab} onTabSelect={(_, data) => setSelectedTab(data.value)}>
                <FC.Tab value="basic">基本资料</FC.Tab>
                <FC.Tab value="service">维修保养</FC.Tab>
                <FC.Tab value="sessions">巡检使用记录</FC.Tab>
                <FC.Tab value="attachments">附件</FC.Tab>
                <FC.Tab value="audit">操作审计</FC.Tab>
            </FC.TabList>

            {selectedTab === "basic" && (
                <>
                    <div className={styles.section}>
                        <FC.Subtitle2>基本信息</FC.Subtitle2>
                        <div className={styles.grid}>
                            <Field label="租户">{detail?.tenantName || detail?.tenantId}</Field>
                            <Field label="资产编号">{detail?.assetCode}</Field>
                            <Field label="名称">{detail?.name}</Field>
                            <Field label="类型">{equipmentTypeLabel(detail?.type)}</Field>
                            <Field label="序列号">{detail?.serialNumber}</Field>
                            <Field label="品牌">{detail?.manufacturer}</Field>
                            <Field label="型号">{detail?.model}</Field>
                        </div>
                    </div>

                    <div className={styles.section}>
                        <FC.Subtitle2>使用信息</FC.Subtitle2>
                        <div className={styles.grid}>
                            <Field label="运行状态">
                                <EquipmentStatusBadge
                                    kind="operationalStatus"
                                    value={detail?.operationalStatus}
                                />
                            </Field>
                            <Field label="状态来源">
                                {detail
                                    ? `${detail.statusSourceType}${detail.statusSourceId ? ` #${detail.statusSourceId}` : ""}`
                                    : null}
                            </Field>
                            <Field label="状态变更时间">{toDateTimeString(detail?.statusChangedAtUtc)}</Field>
                            <Field label="负责人">{detail?.custodianName}</Field>
                            <Field label="存放位置">{detail?.location}</Field>
                            <Field label="购买日期">{formatDateOnly(detail?.purchaseDate)}</Field>
                            <Field label="启用日期">{formatDateOnly(detail?.commissionedDate)}</Field>
                        </div>
                    </div>

                    <div className={styles.section}>
                        <FC.Subtitle2>保修与保养</FC.Subtitle2>
                        <div className={styles.grid}>
                            <Field label="保修状态">
                                <EquipmentStatusBadge
                                    kind="warrantyState"
                                    value={detail?.warrantyState}
                                    daysRemaining={detail?.warrantyDaysRemaining}
                                />
                            </Field>
                            <Field label="保修开始">{formatDateOnly(detail?.warrantyStartDate)}</Field>
                            <Field label="保修截止">{formatDateOnly(detail?.warrantyEndDate)}</Field>
                            <Field label="保修服务商">{detail?.warrantyProvider}</Field>
                            <Field label="保养状态">
                                <EquipmentStatusBadge
                                    kind="maintenanceState"
                                    value={detail?.maintenanceState}
                                    daysRemaining={detail?.maintenanceDaysRemaining}
                                />
                            </Field>
                            <Field label="保养周期（天）">{detail?.maintenanceIntervalDays}</Field>
                            <Field label="最近保养">{formatDateOnly(detail?.lastMaintenanceDate)}</Field>
                            <Field label="下次保养">{formatDateOnly(detail?.nextMaintenanceDate)}</Field>
                        </div>
                        <FC.Divider>保养计划调整</FC.Divider>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    {["生效日期", "下次保养日期", "原因", "状态", "记录时间"].map((label) => (
                                        <th className={styles.cell} key={label}>
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {adjustments.map((row) => (
                                    <tr key={row.id}>
                                        <td className={styles.cell}>{formatDateOnly(row.effectiveDate)}</td>
                                        <td className={styles.cell}>
                                            {formatDateOnly(row.nextMaintenanceDate) || "清除计划"}
                                        </td>
                                        <td className={styles.cell}>{row.reason}</td>
                                        <td className={styles.cell}>{row.isVoided ? "已作废" : "有效"}</td>
                                        <td className={styles.cell}>{toDateTimeString(row.createdAtUtc)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {adjustmentsError && (
                            <FC.MessageBar intent="error">
                                <FC.MessageBarBody>{adjustmentsError}</FC.MessageBarBody>
                            </FC.MessageBar>
                        )}
                        {!adjustmentsError && adjustments.length === 0 && (
                            <div className={styles.label}>暂无保养计划调整记录</div>
                        )}
                    </div>

                    <div className={styles.section}>
                        <FC.Subtitle2>扩展规格与备注</FC.Subtitle2>
                        <Field label={`扩展规格（${detail?.specificationSchemaVersion || "-"}）`}>
                            <div className={styles.mono}>
                                {detail?.specifications ? JSON.stringify(detail.specifications, null, 2) : "-"}
                            </div>
                        </Field>
                        <Field label="备注">{detail?.notes}</Field>
                    </div>

                    <div className={styles.section}>
                        <FC.Subtitle2>记录信息</FC.Subtitle2>
                        <div className={styles.grid}>
                            <Field label="创建时间">{toDateTimeString(detail?.createdAtUtc)}</Field>
                            <Field label="更新时间">{toDateTimeString(detail?.updatedAtUtc)}</Field>
                            <Field label="归档时间">{toDateTimeString(detail?.archivedAtUtc)}</Field>
                            <Field label="版本">{detail ? `v${detail.version}` : null}</Field>
                        </div>
                    </div>
                </>
            )}

            {selectedTab === "service" && (
                <div className={styles.section}>
                    {/* Same component the cross-equipment page renders, pinned to this unit. Two
                        implementations would eventually disagree about what a filter means. */}
                    <ServiceRecordPanel
                        equipmentId={Number(equipmentId)}
                        equipment={detail}
                        canWrite={permissions.canWrite}
                        onChanged={load}
                    />
                </div>
            )}
            {selectedTab === "sessions" && (
                <div className={styles.section}>
                    {/* Keyed like the other per-unit panels: navigating between units must not leave
                        the previous unit's history mounted. */}
                    <EquipmentUsagePanel key={`${equipmentId}:sessions`} equipmentId={Number(equipmentId)} />
                </div>
            )}
            {selectedTab === "attachments" && (
                <div className={styles.section}>
                    {/* Keyed for the same reason as the service-record drawer: navigating between
                        units must not leave the previous unit's attachments mounted. */}
                    <AttachmentPanel
                        key={`${equipmentId}:master`}
                        equipmentId={Number(equipmentId)}
                        canWrite={permissions.canWrite}
                    />
                </div>
            )}
            {selectedTab === "audit" && <PhasePlaceholder phase="第二期" feature="操作审计" />}

            {loading && <FC.Spinner size="tiny" label="加载中..." />}
            {drawer}
        </div>
    );
};
